import { EditRounded } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, IconButton } from '@mui/material';
import { addDays, addMonths, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange, DayPicker, SelectRangeEventHandler } from 'react-day-picker';
import { DEFAULT_START_DATE } from '../../constants';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import useMobileMonthScroll from '../../hooks/useMobileMonthScroll';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { updateDates } from '../../redux/trip/trip.operations';
import { selectChangedDefaultDates } from '../../redux/trip/trip.selectors';
import { Trip } from '../../redux/trip/trip.types';
import {
  doesNewDateRangeExcludeEvents,
  doesNewDateRangeOverlapOriginal,
  getNumberOfDaysWithEvents,
  getNumDaysInDateRange,
} from '../../utils/date-range-picker.utils';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { Modal, Button as SharedButton } from '../shared';

import 'react-day-picker/dist/style.css';
import './DateRangePicker.scss';

interface DateRangePickerProps {
  trip: Trip;
  initialDateRange: DateRange;
  isOpen: boolean;
  openDatePicker: (value: boolean) => void;
  showEditBtn?: boolean;
}

const DAY_MODIFIER_CLASS_NAMES = {
  range_start: 'rangeStart',
  range_middle: 'rangeMiddle',
  range_end: 'rangeEnd',
};

const RANGE_EDGE_STYLE = {
  fontWeight: 'bold',
  backgroundColor: 'rgb(242, 201, 76)',
  borderRadius: '10px',
  color: 'black',
};

const DAY_MODIFIER_STYLES = {
  range_start: RANGE_EDGE_STYLE,
  range_middle: {
    backgroundColor: 'rgb(242, 201, 76 ,0.5)',
    color: 'black',
    borderRadius: '0',
    height: '65%',
  },
  range_end: RANGE_EDGE_STYLE,
};

const formatLabelDate = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const isDefaultDate = (date?: Date) =>
  date != null && date.getFullYear() === DEFAULT_START_DATE.getFullYear();

const resolveRange = (startDateISO: string | undefined, dateRange: DateRange) =>
  startDateISO && !isDefaultDate(dateRange.from) ? dateRange : undefined;

function DateRangePicker({
  trip,
  initialDateRange,
  isOpen,
  openDatePicker,
  showEditBtn = false,
}: DateRangePickerProps) {
  const dispatch = useAppDispatch();
  const isMobile = useMobileMediaQuery();
  const isChangedDefaultDates = useAppSelector(selectChangedDefaultDates);
  const [range, setRange] = useState<DateRange | undefined>(() =>
    resolveRange(trip.startDateISO, initialDateRange)
  );
  const [newDateRange, setNewDateRange] = useState<DateRange | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  // True once the user edits the selection, so late-arriving trip dates don't clobber the draft.
  const isDraftDirty = useRef(false);
  const lastIsOpen = useRef(false);

  // The selection is a draft of the trip's dates: reseed it on every open, and whenever the trip
  // dates change (e.g. the store finishing a save) unless the user is mid-edit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the ISO strings, not the per-render Date objects
  useEffect(() => {
    // A fresh open (or close) discards any earlier edits; later date changes only reseed an
    // untouched draft.
    if (lastIsOpen.current !== isOpen) {
      lastIsOpen.current = isOpen;
      isDraftDirty.current = false;
    }
    if (!isDraftDirty.current) {
      setRange(resolveRange(trip.startDateISO, initialDateRange));
    }
  }, [isOpen, trip.startDateISO, trip.endDateISO]);

  const startDate = useMemo(
    () => (trip.startDateISO ? parseISO(trip.startDateISO) : DEFAULT_START_DATE),
    [trip.startDateISO]
  );
  const endDate = useMemo(
    () => (trip.endDateISO ? parseISO(trip.endDateISO) : addDays(DEFAULT_START_DATE, 3)),
    [trip.endDateISO]
  );
  const defaultMonth = isDefaultDate(startDate)
    ? new Date()
    : new Date(startDate.getFullYear(), startDate.getMonth());

  const tripDateRange = useMemo(() => ({ from: startDate, to: endDate }), [startDate, endDate]);

  const changeDates = useCallback(() => {
    if (newDateRange?.from && newDateRange?.to) {
      dispatch(
        updateDates({
          tripId: trip.id,
          oldDateRange: tripDateRange,
          newDateRange,
          days: trip.days,
        })
      );
      setNewDateRange(undefined);
    }
  }, [dispatch, trip.id, trip.days, tripDateRange, newDateRange]);

  useEffect(() => {
    if (isChangedDefaultDates && newDateRange) {
      changeDates();
    }
  }, [isChangedDefaultDates, changeDates, newDateRange]);

  const handleOnDateRangeSelected = (dateRange: DateRange) => {
    if (dateRange.from && dateRange.to) {
      setNewDateRange(dateRange);
      setDialogOpen(true);
    }
    openDatePicker(false);
  };

  const handleDateRangeSelected: SelectRangeEventHandler = (selectedRange) => {
    isDraftDirty.current = true;
    setRange(selectedRange);
  };

  const handleSaveDateRange = () => {
    if (isOpen && range) {
      handleOnDateRangeSelected(range);
    }
    openDatePicker(false);
  };

  // The draft is reseeded from the trip dates on the next open, so there's nothing to restore.
  const handleCancelDateChange = () => {
    openDatePicker(false);
    setNewDateRange(undefined);
  };

  const handleClearDates = () => {
    isDraftDirty.current = true;
    setNewDateRange(undefined);
    setRange(undefined);
  };

  const handleConfirmDateChangeFromDialog = () => {
    setDialogOpen(false);
    changeDates();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  /** Render different Dialog Popup when confirming date change when:
   * (LONGER DIALOG WITH WARNING) - itinerary causes date change conflict
   *  - new date range is completely different from initial date range && there are itinerary events
   *
   * (SHORTER BASIC DIALOG) - itinerary isn't affected
   *  - there are no itinerary events
   *  - initial date range has less days than new date range
   *  - initial date range has more days but # of days with itinerary events is less than # of days in new date range
   *
   */
  const numDaysWithEvents = getNumberOfDaysWithEvents(trip);

  // Calculate the number of days in initialDateRange and newDateRange to compare difference
  const numDaysInitialRange = useMemo(
    () => getNumDaysInDateRange(initialDateRange || tripDateRange),
    [initialDateRange, tripDateRange]
  );
  const numDaysNewRange = useMemo(
    () => getNumDaysInDateRange(newDateRange || tripDateRange),
    [newDateRange, tripDateRange]
  );

  const thereIsConflict = newDateRange
    ? doesNewDateRangeExcludeEvents(trip, initialDateRange, newDateRange)
    : false;

  // Display different copy for dialog popup if first time setting dates or updating existing dates
  const isFirstTimeSettingDates = isDefaultDate(startDate);
  const simpleChangeDateMsg = isFirstTimeSettingDates
    ? 'Just double checking those dates are correct. Happy adventuring!'
    : 'Are you sure you want to change the dates to the selected range?';

  const hasEventsOutsideNewRange =
    (!doesNewDateRangeOverlapOriginal(initialDateRange, newDateRange) && numDaysWithEvents !== 0) ||
    thereIsConflict;

  const hasNoEventsOrNewRangeIsLarger =
    Object.keys(trip.events).length === 0 ||
    numDaysInitialRange <= numDaysNewRange ||
    (numDaysInitialRange > numDaysNewRange && numDaysWithEvents <= numDaysNewRange) ||
    initialDateRange.from === DEFAULT_START_DATE;

  const warningMessage = (
    <>
      <p>
        You currently have places added in your itinerary that do not fall within the new date
        range. These events will be removed from the itinerary.
      </p>
      <p className='styled-dialog-subtext'>
        *To avoid any unwanted changes, check the Itinerary tab before changing the date.
      </p>
    </>
  );

  const dialogContent: JSX.Element =
    hasEventsOutsideNewRange || !hasNoEventsOrNewRangeIsLarger ? (
      warningMessage
    ) : (
      <p>{simpleChangeDateMsg}</p>
    );

  const isSaveDisabled =
    JSON.stringify(range) === JSON.stringify(initialDateRange) || range === undefined;

  // Both labels key off the start date: a placeholder year means no dates are set yet.
  const hasSelection = Boolean(range?.from) && !isDefaultDate(range?.from);
  const renderDateLabel = (date: Date | undefined, placeholder: string) => (
    <span className={`date-range-label${date && hasSelection ? ' selected' : ''}`}>
      {date && hasSelection ? formatLabelDate(date) : placeholder}
    </span>
  );

  const selectedDatesLabels = (
    <div className='date-range-span'>
      {renderDateLabel(range?.from, 'Start Date')}
      <ArrowForwardIcon />
      {renderDateLabel(range?.to, 'End Date')}
    </div>
  );

  const anchorKey = `${defaultMonth.getFullYear()}-${defaultMonth.getMonth()}`;
  const { monthCount, pastMonthCount, attachScrollLoader } = useMobileMonthScroll({
    isOpen,
    anchorKey,
  });

  const dayPicker = (
    <DayPicker
      className='styled-day-picker'
      mode='range'
      selected={range}
      onSelect={handleDateRangeSelected}
      numberOfMonths={isMobile ? monthCount : 2}
      {...(isMobile
        ? { month: addMonths(defaultMonth, -pastMonthCount), onMonthChange: () => undefined }
        : { defaultMonth })}
      modifiersClassNames={DAY_MODIFIER_CLASS_NAMES}
      modifiersStyles={DAY_MODIFIER_STYLES}
    />
  );

  // Reset only applies once the selection differs from the trip's current dates.
  const hasPickedDate =
    Boolean(range?.from) && JSON.stringify(range) !== JSON.stringify(initialDateRange);

  const confirmDialog = (
    <ConfirmDialog
      open={dialogOpen}
      title={isFirstTimeSettingDates ? 'Confirm trip dates?' : 'Update trip dates?'}
      confirmLabel={isFirstTimeSettingDates ? 'Confirm' : 'Update'}
      onCancel={handleDialogClose}
      onConfirm={handleConfirmDateChangeFromDialog}
    >
      {dialogContent}
    </ConfirmDialog>
  );

  if (isMobile) {
    return (
      <>
        <Modal
          open={isOpen}
          onClose={handleCancelDateChange}
          mobile='full'
          transition='slide-up'
          title='Trip Dates'
          className='date-range-picker-mobile'
          footer={
            <>
              <SharedButton
                tone='plain'
                quiet
                onClick={hasPickedDate ? handleClearDates : handleCancelDateChange}
              >
                {hasPickedDate ? 'Reset' : 'Cancel'}
              </SharedButton>
              <SharedButton
                outline
                tone='neutral'
                onClick={handleSaveDateRange}
                disabled={isSaveDisabled}
              >
                Save
              </SharedButton>
            </>
          }
        >
          {selectedDatesLabels}
          <div ref={attachScrollLoader} />
          {dayPicker}
        </Modal>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <IconButton
        aria-label='edit'
        className={`date-range-edit-btn${showEditBtn ? '' : ' hidden'}`}
        onClick={() => openDatePicker(!isOpen)}
      >
        <EditRounded />
      </IconButton>
      {isOpen && (
        <div className='date-range-picker-container'>
          <div className='selected-dates-text-container'>
            {selectedDatesLabels}
            <Button
              className='clear-dates-btn button'
              onClick={handleClearDates}
            >
              Clear Dates
            </Button>
          </div>
          {dayPicker}
          <div className='btns-container'>
            <Button
              className='cancel-btn'
              onClick={handleCancelDateChange}
            >
              Cancel
            </Button>
            <Button
              className={`save-btn button ${isSaveDisabled ? ' disabled' : ''}`}
              onClick={handleSaveDateRange}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </div>
        </div>
      )}
      {confirmDialog}
    </>
  );
}

DateRangePicker.displayName = 'DateRangePicker';
export default DateRangePicker;
